"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"

import { apiMarkAllRead, apiMarkRead, fetchNotifications } from "../push"
import type { AppNotification, NotificationPrefs } from "../types"

interface NotificationsState {
  prefs: NotificationPrefs
  items: AppNotification[]
  /** Server-reported unread count (preferred over locally-computed). */
  serverUnread: number
  /** Bumped on every local mutation so a stale in-flight fetch can be dropped. */
  mutationGen: number
  togglePref: (key: keyof NotificationPrefs) => void
  /** Refetch the feed from Laravel (source of truth) into the cache. */
  hydrateFromServer: () => Promise<void>
  markAllRead: () => void
  markRead: (id: string) => void
  /** Unread badge: combines server count with optimistic local edits. */
  unreadCount: () => number
}

export const useNotificationsStore = create<NotificationsState>()(
  persist(
    (set, get) => ({
      prefs: {
        bookingReminders: true,
        membershipExpiry: true,
        promotions: false,
      },
      // Cache only — populated from the server. Persisted for instant
      // render while the next fetch resolves (stale-while-revalidate).
      items: [],
      serverUnread: 0,
      mutationGen: 0,
      togglePref: (key) =>
        set((s) => ({ prefs: { ...s.prefs, [key]: !s.prefs[key] } })),
      hydrateFromServer: async () => {
        const gen = get().mutationGen
        try {
          const { items, unread } = await fetchNotifications()
          // A local mark-read raced this fetch — its response is stale and would
          // re-introduce already-cleared unreads. Keep the optimistic state.
          if (get().mutationGen !== gen) return
          set({ items, serverUnread: unread })
        } catch {
          // offline / API down — keep the cached items
        }
      },
      markAllRead: () => {
        set((s) => ({
          items: s.items.map((n) => ({ ...n, read: true })),
          serverUnread: 0,
          mutationGen: s.mutationGen + 1,
        }))
        apiMarkAllRead().catch(() => {})
      },
      markRead: (id) => {
        set((s) => {
          const target = s.items.find((n) => n.id === id)
          const wasUnread = target?.read === false
          return {
            items: s.items.map((n) => (n.id === id ? { ...n, read: true } : n)),
            serverUnread: wasUnread
              ? Math.max(0, s.serverUnread - 1)
              : s.serverUnread,
            mutationGen: s.mutationGen + 1,
          }
        })
        apiMarkRead(id).catch(() => {})
      },
      unreadCount: () => {
        const s = get()
        // Use whichever is larger: the locally-computed count covers items the
        // user has not yet read in the cached list; the server count is
        // authoritative for items not yet hydrated. Max() avoids badge flicker.
        const local = s.items.filter((n) => !n.read).length
        return Math.max(local, s.serverUnread)
      },
    }),
    {
      name: "psc-notifications",
      // serverUnread is transient — recomputed on every fetch — so we don't
      // persist it. prefs + items still hydrate for instant render.
      partialize: (s) => ({ prefs: s.prefs, items: s.items }),
    }
  )
)
